<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  ContractManagementDateType,
  TaskStatus,
  type CalendarPreviewQuery,
  type ContractManagementContractFileInDb,
  type ContractManagementContractInDb,
  type ContractManagementDateInDb,
  type ContractManagementExtractionInDb,
  type ContractManagementViewColumnInDb,
} from '@/api';
import { CalendarService } from '@/services/calendarService';
import { buildExtractionCell, type CalendarPreviewFilters } from '@/utils/calendarOccurrences';
import { formatDateForDisplay } from '@/utils/datetimeUtils';
import { formatDisplayValue, isEffectivelyEmpty } from '@/utils/displayAiResponse';
import { DATE_TYPE_LABELS } from '@/services/contractManagement/dateRow';
import { logger } from '@/utils/logger';
import { useContractManagementExtractionWebSocket } from '@/composables/useContractManagementExtractionWebSocket';
import ContractManagementExtractionDetailsModal from '@/components/contractManagement/ContractManagementExtractionDetailsModal.vue';
import GaiaButton from '../../../base/GaiaButton.vue';
import GaiaLink from '../../../base/GaiaLink.vue';
import GaiaStatusIndicator from '../../../base/GaiaStatusIndicator.vue';
import GaiaText from '../../../base/GaiaText.vue';
import GaiaTooltip from '../../../base/GaiaTooltip.vue';
import GaiaDataTable, { type GaiaDataColumn } from '../../../composite/GaiaDataTable.vue';
import GaiaEmptyState from '../../../composite/GaiaEmptyState.vue';
import GaiaPagination from '../../../composite/GaiaPagination.vue';
import GaiaTableSkeleton from '../../../composite/GaiaTableSkeleton.vue';
import { gaiaNeutral } from '../../../themes/tokens';
import { taskStatusMap } from '../../../statusMaps';
import { contractFileRoute, contractRoute } from '@/router/links';
import {
  ACCESS_PERMISSION_READ,
  ACCESS_PERMISSION_WRITE,
  deniedReason,
} from '@/utils/accessPermission';

/**
 * The dates a calendar carries, per contract file: its fixed contract dates
 * and what its prompt extracted.
 *
 * A preview of what the digest and the .ics feed will carry, so both cells are
 * live: the contract name opens the contract, the file name under it opens
 * the contract file, and the date opens the extraction so a wrong answer can
 * be corrected on the spot.
 */
const props = defineProps<{
  calendarUid: string;
  filters: CalendarPreviewFilters;
  /** The calendar's fixed date types — one column each. */
  dateTypes: ContractManagementDateType[];
  /** Whether the calendar runs a prompt — adds the extracted-dates column. */
  hasPrompt: boolean;
}>();

/** A date was corrected or finished extracting, so a grid beside the list is stale. */
const emit = defineEmits<{ changed: [] }>();

interface PreviewRow {
  /**
   * `contract` is optional until the preview endpoint returns each file with
   * its contract (`ContractManagementContractFileInDbExtended`); without it
   * the cell falls back to the file name alone.
   */
  file: ContractManagementContractFileInDb & { contract?: ContractManagementContractInDb };
  extraction: ContractManagementExtractionInDb | null;
  fixedDates: ContractManagementDateInDb[];
}

const rows = ref<PreviewRow[]>([]);
const resultCount = ref<number>(0);
const loading = ref<boolean>(false);
const loadError = ref<string | null>(null);

const page = ref<number>(1);
const pageSize = ref<number>(10);
// The list arrives date-ascending — the next deadline first, which is the
// whole point of the page — so that is the default, not a user choice.
const sortKey = ref<NonNullable<CalendarPreviewQuery['sort_key']>>('date');
const sortDirection = ref<NonNullable<CalendarPreviewQuery['sort_direction']>>('asc');
const sortActivated = ref<boolean>(false);

const showEditModal = ref<boolean>(false);
const editingCell = ref<{
  columnUid: string;
  extraction: ContractManagementExtractionInDb | null;
  column: ContractManagementViewColumnInDb | null;
  fileUid?: string;
}>({ columnUid: '', extraction: null, column: null });

// The term's own order — effective from, then effective to — whatever order
// the calendar stored its types in. Headers and cells both read it.
const orderedDateTypes = computed(() =>
  Object.values(ContractManagementDateType).filter(type => props.dateTypes.includes(type))
);

// Only the date sources the calendar uses get a column, so a fixed-dates-only
// calendar has no Prompt column that is "—" on every row.
const tableColumns = computed<GaiaDataColumn[]>(() => [
  // The endpoint's `name` sort is the parent contract's name, which is what
  // this column leads with.
  { key: 'name', label: 'Contract', width: 'minmax(240px,1.6fr)', sortable: true },
  { key: 'type', label: 'Type', width: 'minmax(120px,0.7fr)' },
  ...orderedDateTypes.value.map(type => ({
    key: `fixed:${type}`,
    label: DATE_TYPE_LABELS[type] ?? type,
    width: 'minmax(120px,0.7fr)',
  })),
  // The endpoint's `date` sort orders by the extracted date.
  ...(props.hasPrompt
    ? [{ key: 'date', label: 'Prompt', width: 'minmax(180px,1.2fr)', sortable: true }]
    : []),
]);

const skeletonCells = computed(() => ['70%', ...tableColumns.value.slice(1).map(() => '45%')]);

// A row whose extraction is still null is queued: the start task hasn't
// created it yet. Those rows are polled for, backing off, until the
// extraction exists — its uid then joins the websocket subscription below.
const POLL_INITIAL_MS = 5_000;
const POLL_BACKOFF = 1.5;
const POLL_MAX_MS = 30_000;
const POLL_CAP_MS = 5 * 60_000;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollDelay = POLL_INITIAL_MS;
let pollStartedAt = 0;
// Only the latest request may write rows, so a slow poll can't overwrite a
// page, sort or filter change that landed after it.
let requestId = 0;

function stopPolling(): void {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function schedulePoll(): void {
  stopPolling();
  const hasQueued = props.hasPrompt && rows.value.some(row => row.extraction === null);
  if (!hasQueued || loadError.value || Date.now() - pollStartedAt >= POLL_CAP_MS) return;
  pollTimer = setTimeout(() => {
    pollTimer = null;
    void fetchPreview({ silent: true });
  }, pollDelay);
  pollDelay = Math.min(pollDelay * POLL_BACKOFF, POLL_MAX_MS);
}

/** A silent fetch is a poll: no skeleton, and an error only logs. */
async function fetchPreview({ silent = false }: { silent?: boolean } = {}): Promise<void> {
  const current = ++requestId;
  if (!silent) {
    stopPolling();
    pollDelay = POLL_INITIAL_MS;
    pollStartedAt = Date.now();
    loading.value = true;
    loadError.value = null;
  }
  try {
    const query: CalendarPreviewQuery = {
      text: props.filters.text,
      limit: pageSize.value,
      offset: (page.value - 1) * pageSize.value,
      sort_key: sortKey.value,
      sort_direction: sortDirection.value,
      only_value_extracted: props.filters.onlyValueExtracted,
      only_future: props.filters.onlyFuture,
      column_filters: props.filters.types.length > 0 ? { type: props.filters.types } : {},
    };
    const result = await CalendarService.preview(props.calendarUid, query);
    if (current !== requestId) return;
    const queued = new Set(rows.value.filter(row => !row.extraction).map(row => row.file.uid));
    rows.value = result.results.map(([file, extraction, fixedDates]) => ({
      file,
      extraction,
      fixedDates,
    }));
    // A poll can find a queued extraction already finished, skipping the websocket.
    if (
      silent &&
      rows.value.some(
        row => queued.has(row.file.uid) && row.extraction && !isExtracting(row.extraction)
      )
    ) {
      emit('changed');
    }
    resultCount.value = result.result_count;
  } catch (error) {
    if (current !== requestId) return;
    logger.error('Error loading calendar preview:', error);
    if (!silent) {
      loadError.value = error instanceof Error ? error.message : 'Failed to load calendar preview';
    }
  } finally {
    if (current === requestId) {
      loading.value = false;
      schedulePoll();
    }
  }
}

function isExtracting(extraction: ContractManagementExtractionInDb): boolean {
  return (
    extraction.processing_state === TaskStatus.PENDING ||
    extraction.processing_state === TaskStatus.RUNNING
  );
}

// Extractions that exist but haven't finished get their result pushed over the
// websocket and patched in place; a finished one drops out and unsubscribes.
const extractingUids = computed(() =>
  rows.value.flatMap(row =>
    row.extraction && isExtracting(row.extraction) ? [row.extraction.uid] : []
  )
);

useContractManagementExtractionWebSocket({
  extractionUids: () => extractingUids.value,
  onExtractionUpdated: (uid, data) => {
    for (const row of rows.value) {
      if (row.extraction?.uid !== uid) continue;
      Object.assign(row.extraction, data);
      if (!isExtracting(row.extraction)) emit('changed');
    }
  },
});

onBeforeUnmount(stopPolling);

// A narrower result can leave the current page past the end, so start over.
watch(
  () => props.filters,
  () => {
    page.value = 1;
    void fetchPreview();
  }
);

function onPage(value: number): void {
  page.value = value;
  void fetchPreview();
}

function onPageSize(value: number): void {
  pageSize.value = value;
  page.value = 1;
  void fetchPreview();
}

function onSort(key: string): void {
  if (key !== 'name' && key !== 'date') return;
  if (sortActivated.value && sortKey.value === key) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortActivated.value = true;
    sortKey.value = key;
    // A deadline list opens with the nearest date; a name opens A–Z.
    sortDirection.value = 'asc';
  }
  page.value = 1;
  void fetchPreview();
}

/** Open the extraction behind a date. */
function openExtraction(row: PreviewRow): void {
  if (!row.extraction) return;
  editingCell.value = buildExtractionCell(row.extraction, row.file.uid);
  showEditModal.value = true;
}

/** The row's date of one fixed type, or null when the contract has none. */
function fixedDateOf(row: PreviewRow, type: ContractManagementDateType): string | null {
  return row.fixedDates.find(fixedDate => fixedDate.type === type)?.date ?? null;
}

async function handleExtractionUpdate(): Promise<void> {
  showEditModal.value = false;
  emit('changed');
  await fetchPreview();
}

const emptyCell = '—';

// File names rarely have spaces to wrap at, so a long one would run into the
// next column; it truncates instead, with the full name in the tooltip.
const truncateStyle = {
  display: 'block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  // An inert link renders as a button, which would centre it.
  textAlign: 'left',
};

/*
 * A calendar's viewers see its dates without necessarily being able to open
 * what they come from: the links go inert, with the reason as their title.
 * A contract file carries its contract's bits, on its vault file.
 */
function contractBlock(row: PreviewRow): string | null {
  return deniedReason(row.file.contract?.permissions, ACCESS_PERMISSION_READ);
}

function fileBlock(row: PreviewRow): string | null {
  return deniedReason(
    row.file.vault_file?.permissions ?? row.file.contract?.permissions,
    ACCESS_PERMISSION_READ
  );
}

/** Correcting the extracted date writes to the file, so it needs WRITE on it. */
function extractionBlock(row: PreviewRow): string | null {
  return deniedReason(
    row.file.vault_file?.permissions ?? row.file.contract?.permissions,
    ACCESS_PERMISSION_WRITE
  );
}

function fileName(file: ContractManagementContractFileInDb): string {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return file.vault_file?.current_revision?.file_name || 'Untitled file';
}

function extractionValue(extraction: ContractManagementExtractionInDb | null): string {
  if (!extraction) return emptyCell;
  const value = extraction.human_overwrite?.value ?? extraction.value;
  // The prompt ran but found nothing — say so instead of the generic "Empty".
  if (isEffectivelyEmpty(value)) return 'No date';
  return formatDisplayValue(value, extraction.response_type);
}

/**
 * What the Prompt cell shows. A manual override is a value even while a rerun
 * is still extracting or after it failed.
 */
function promptCellState(
  extraction: ContractManagementExtractionInDb | null
): 'queued' | 'extracting' | 'failed' | 'value' {
  if (!extraction) return 'queued';
  if (extraction.human_overwrite?.value != null) return 'value';
  if (isExtracting(extraction)) return 'extracting';
  if (extraction.processing_state === TaskStatus.FAILED) return 'failed';
  return 'value';
}

const hasFilters = computed(
  () =>
    props.filters.text.trim().length > 0 ||
    props.filters.onlyValueExtracted ||
    props.filters.onlyFuture ||
    props.filters.types.length > 0
);
const showRows = computed(() => !loading.value && !loadError.value && rows.value.length > 0);
const showEmpty = computed(() => !loading.value && !loadError.value && rows.value.length === 0);

function refresh(): void {
  void fetchPreview();
}
defineExpose({ refresh });

onMounted(fetchPreview);

const rowBorder = gaiaNeutral.borderSoft;
const rowHover = gaiaNeutral.surfaceHover;
</script>

<template>
  <n-flex vertical :size="12" :wrap="false">
    <gaia-data-table
      :columns="tableColumns"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
      :sort-active="sortActivated"
      :action-width="0"
      @sort="onSort"
    >
      <template #default="{ rowStyle }">
        <gaia-table-skeleton
          v-if="loading"
          :action="false"
          :row-style="rowStyle"
          :row-height="52"
          :cells="skeletonCells"
          :rows="4"
        />

        <gaia-empty-state v-else-if="loadError" status="alert" title="Couldn’t load the preview">
          {{ loadError }}
          <template #actions>
            <gaia-button type="primary" size="small" @click="refresh">Retry</gaia-button>
          </template>
        </gaia-empty-state>

        <gaia-empty-state
          v-else-if="showEmpty"
          dashed
          :title="hasFilters ? 'No extracted dates match this search' : 'No dates extracted yet'"
        >
          {{
            hasFilters
              ? 'Clear the search or the filters to see every extracted date.'
              : 'Fixed dates appear once the contracts in scope have them, extracted dates once Insights has run this calendar’s prompt.'
          }}
        </gaia-empty-state>

        <template v-else-if="showRows">
          <div v-for="row in rows" :key="row.file.uid" class="row" :style="rowStyle">
            <n-flex vertical :size="2" :wrap="false" :style="{ minWidth: 0 }">
              <template v-if="row.file.contract">
                <gaia-link
                  :underline="false"
                  :to="contractBlock(row) ? undefined : contractRoute(row.file.contract.uid)"
                  :disabled="contractBlock(row) !== null"
                  :title="contractBlock(row) ?? undefined"
                  :style="{ textAlign: 'left' }"
                >
                  {{ row.file.contract.name }}
                </gaia-link>
                <gaia-link
                  tone="muted"
                  :underline="false"
                  :to="fileBlock(row) ? undefined : contractFileRoute(row.file.uid)"
                  :disabled="fileBlock(row) !== null"
                  :title="fileBlock(row) ?? fileName(row.file)"
                  :style="truncateStyle"
                >
                  {{ fileName(row.file) }}
                </gaia-link>
              </template>
              <gaia-link
                v-else
                :underline="false"
                :to="fileBlock(row) ? undefined : contractFileRoute(row.file.uid)"
                :disabled="fileBlock(row) !== null"
                :title="fileBlock(row) ?? fileName(row.file)"
                :style="truncateStyle"
              >
                {{ fileName(row.file) }}
              </gaia-link>
            </n-flex>
            <gaia-text size="dense" :tone="row.file.contract?.contract_type ? 'text' : 'muted'">
              {{ row.file.contract?.contract_type?.name ?? emptyCell }}
            </gaia-text>
            <gaia-text
              v-for="type in orderedDateTypes"
              :key="type"
              size="dense"
              :tone="fixedDateOf(row, type) ? 'text' : 'muted'"
              :style="{ fontVariantNumeric: 'tabular-nums' }"
            >
              {{
                fixedDateOf(row, type) ? formatDateForDisplay(fixedDateOf(row, type)) : emptyCell
              }}
            </gaia-text>
            <template v-if="props.hasPrompt">
              <gaia-status-indicator
                v-if="promptCellState(row.extraction) === 'queued'"
                :status="taskStatusMap[TaskStatus.PENDING].status"
              >
                Queued
              </gaia-status-indicator>
              <gaia-status-indicator
                v-else-if="promptCellState(row.extraction) === 'extracting'"
                :status="
                  taskStatusMap[row.extraction?.processing_state ?? TaskStatus.RUNNING].status
                "
              >
                Extracting…
              </gaia-status-indicator>
              <gaia-tooltip
                v-else-if="promptCellState(row.extraction) === 'failed'"
                :disabled="!row.extraction?.error_text"
              >
                <template #trigger>
                  <gaia-link
                    :underline="false"
                    :disabled="extractionBlock(row) !== null"
                    :style="{ textAlign: 'left' }"
                    @click="openExtraction(row)"
                  >
                    <gaia-status-indicator :status="taskStatusMap[TaskStatus.FAILED].status">
                      Failed
                    </gaia-status-indicator>
                  </gaia-link>
                </template>
                {{ row.extraction?.error_text }}
              </gaia-tooltip>
              <gaia-link
                v-else
                :underline="false"
                :disabled="extractionBlock(row) !== null"
                :title="extractionBlock(row) ?? undefined"
                :style="{ fontVariantNumeric: 'tabular-nums', textAlign: 'left' }"
                @click="openExtraction(row)"
              >
                {{ extractionValue(row.extraction) }}
              </gaia-link>
            </template>
          </div>
        </template>
      </template>

      <template v-if="showRows" #footer>
        <gaia-pagination
          :page="page"
          :page-size="pageSize"
          :total="resultCount"
          @update:page="onPage"
          @update:page-size="onPageSize"
        />
      </template>
    </gaia-data-table>

    <contract-management-extraction-details-modal
      v-model:show="showEditModal"
      :cell-data="editingCell"
      :on-data-update="handleExtractionUpdate"
    />
  </n-flex>
</template>

<style scoped>
/* A body row rides the grid template GaiaDataTable hands out through the slot,
   so only the row's own chrome lives here. */
.row {
  display: grid;
  align-items: center;
  gap: 16px;
  /* At least the skeleton's height; the contract + file pair grows it. */
  min-height: 52px;
  padding: 8px 18px;
  border-bottom: 1px solid v-bind(rowBorder);
}

.row:hover {
  background: v-bind(rowHover);
}
</style>
